import * as Sentry from '@sentry/browser';
import config from 'config/config';
import { ApiError } from 'utils/error';

export function isNetworkError(error: ApiError | Error | string) {
  if (typeof error !== 'string') {
    // https://medium.com/vinh-rocks/how-to-handle-networkerror-when-using-fetch-ff2663220435
    if ('name' in error && error.name === 'NetworkError') {
      return true;
    }
  }
  const errorMessage = typeof error === 'string' ? error : error.message;
  if (typeof errorMessage !== 'string') {
    return false;
  }
  return (
    // Chrome, Edge, Brave: Failed to fetch
    errorMessage === 'Failed to fetch' ||
    // Firefox: NetworkError when attempting to fetch resource
    errorMessage.includes('NetworkError') ||
    // Safari: Load failed
    errorMessage === 'Load failed' ||
    // Service worker: error
    errorMessage === 'Response served by service worker is an error'
  );
}

const isDebuggingSentry = !!config.sentryDebugMode;

Sentry.init({
  enabled: process.env.NODE_ENV !== 'development',
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  tracesSampleRate: 0.01,
  sampleRate: 0.1,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: isDebuggingSentry,

  beforeSend(event, hint) {
    const error = hint.originalException;
    // Assuming error is of type ApiError. This only works because toApiError function implements statusCode.
    if (
      error &&
      typeof error === 'object' &&
      'statusCode' in error &&
      typeof error.statusCode === 'number'
    ) {
      // Generally we should be able to ignore this.
      // 401 and 404 errors will happen often. Preferably 400 errors shouldn't happen, but even if it occurs, that'd be user error. 429 errors are negligible. 500 errors are already logged in the server side.
      return null;
    }
    // Ignore network errors
    if (isNetworkError(error as any)) {
      return null;
    }
    return event; // Otherwise, send the event
  },
});
